import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useNavigate } from "react-router-dom";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import { getFormattedDateTime } from "../../utils/getFormattedDateTime";

interface PropsType {
  userState: userState;
}

interface BookCafePolicy {
  availabilityStatus?: "OPERATING" | "UNDER_REPAIR" | "OPENING_SOON";
  bookingEnabled: boolean;
  openTime: string;
  closeTime: string;
  lastStartTime: string;
  regularHourlyPrice?: number;
  hourlyPrice: number;
  maxAdvanceDays: number;
  maxContiguousHours: number;
  serverNow: string;
}

const getAvailabilityStatus = (policy: BookCafePolicy | null) =>
  policy?.availabilityStatus ?? (policy?.bookingEnabled ? "OPERATING" : "OPENING_SOON");

const getReservationButtonLabel = (policy: BookCafePolicy | null) => {
  const status = getAvailabilityStatus(policy);
  if (status === "UNDER_REPAIR") return "수리중";
  if (status === "OPENING_SOON") return "오픈 예정";
  return "예약 신청";
};

const mainApi = new HttpMainApi();

const BOOK_CAFE_ROLLING_MESSAGES = [
  { title: "영화관", description: "빔프로젝터와 대형 스크린을 이용해요" },
  { title: "파티룸", description: "아이들 생일잔치를 위한 파티룸" },
  { title: "보드게임", description: "30여 가지 보드게임을 함께 즐겨요" },
  { title: "놀이방", description: "손님과 식사 후 아이들이 놀기 좋은 놀이방" },
  { title: "스포츠 관람", description: "축구·야구 경기를 함께 응원해요" },
];

const formatDateKey = (date: Date) =>
  `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}`;

const parseDateKey = (dateKey: string) =>
  new Date(Number(dateKey.slice(0, 4)), Number(dateKey.slice(4, 6)) - 1, Number(dateKey.slice(6, 8)));

const addDays = (date: Date, amount: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
};

const formatDateLabel = (dateKey: string) =>
  `${dateKey.slice(0, 4)}년 ${dateKey.slice(4, 6)}월 ${dateKey.slice(6, 8)}일`;

const addHour = (time: string) => `${String(Number(time.slice(0, 2)) + 1).padStart(2, "0")}:00`;

const buildSlots = (openTime: string, lastStartTime: string) => {
  const start = Number(openTime.slice(0, 2));
  const end = Number(lastStartTime.slice(0, 2));
  return Array.from({ length: end - start + 1 }, (_, index) => `${String(start + index).padStart(2, "0")}:00`);
};

const BOOK_CAFE_PUBLIC_OPEN_TIME = "10:00";
const BOOK_CAFE_PUBLIC_CLOSE_TIME = "22:00";
const BOOK_CAFE_PUBLIC_LAST_START_TIME = "21:00";
const BOOK_CAFE_PUBLIC_SLOTS = buildSlots(BOOK_CAFE_PUBLIC_OPEN_TIME, BOOK_CAFE_PUBLIC_LAST_START_TIME);

const areConsecutive = (slots: string[]) =>
  slots.every((slot, index) => index === 0 || Number(slot.slice(0, 2)) - Number(slots[index - 1].slice(0, 2)) === 1);

const ReservationBookCafe = ({ userState }: PropsType) => {
  const navigate = useNavigate();
  const [policy, setPolicy] = useState<BookCafePolicy | null>(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [reservedSlots, setReservedSlots] = useState<string[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const loadPolicy = async () => {
      setIsLoading(true);
      const res = await mainApi.get_book_cafe_policy({});
      if (!active) return;
      if (res.code === "200") {
        const nextPolicy = res.response as BookCafePolicy;
        setPolicy(nextPolicy);
        setSelectedDate(nextPolicy.serverNow.slice(0, 10).replace(/-/g, ""));
      } else {
        setPolicy(null);
      }
      setIsLoading(false);
    };
    void loadPolicy();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!policy?.bookingEnabled || !selectedDate) {
      setReservedSlots([]);
      return;
    }
    let active = true;
    const loadAvailability = async () => {
      setIsAvailabilityLoading(true);
      setSelectedSlots([]);
      const res = await mainApi.get_book_cafe_day_availability({ date: selectedDate });
      if (!active) return;
      setReservedSlots(res.code === "200" && Array.isArray(res.response?.reservedSlots) ? res.response.reservedSlots : []);
      setIsAvailabilityLoading(false);
    };
    void loadAvailability();
    return () => {
      active = false;
    };
  }, [policy?.bookingEnabled, selectedDate]);

  const serverDate = useMemo(
    () => (policy ? new Date(policy.serverNow) : new Date()),
    [policy],
  );
  const minDate = useMemo(
    () => new Date(serverDate.getFullYear(), serverDate.getMonth(), serverDate.getDate()),
    [serverDate],
  );
  const maxDate = useMemo(() => addDays(minDate, policy?.maxAdvanceDays ?? 30), [minDate, policy?.maxAdvanceDays]);
  const slots = BOOK_CAFE_PUBLIC_SLOTS;

  const isPastSlot = (slot: string) => {
    if (!policy || selectedDate !== formatDateKey(serverDate)) return false;
    const slotDate = new Date(
      `${selectedDate.slice(0, 4)}-${selectedDate.slice(4, 6)}-${selectedDate.slice(6, 8)}T${slot}:00+09:00`,
    );
    return slotDate.getTime() + 60 * 60 * 1000 <= serverDate.getTime();
  };

  const toggleSlot = (slot: string) => {
    const candidate = selectedSlots.includes(slot)
      ? selectedSlots.filter((item) => item !== slot)
      : [...selectedSlots, slot];
    const sorted = candidate.sort();
    const maxContiguousHours = policy?.maxContiguousHours ?? 5;
    if (sorted.length > maxContiguousHours) {
      window.alert(`한 번에 최대 ${maxContiguousHours}시간까지 예약할 수 있습니다.`);
      return;
    }
    if (sorted.length > 1 && !areConsecutive(sorted)) {
      window.alert("연속된 시간만 선택할 수 있습니다.");
      return;
    }
    setSelectedSlots(sorted);
  };

  const saveReservation = async () => {
    if (!policy?.bookingEnabled || selectedSlots.length === 0 || isAvailabilityLoading || isSaving) return;
    setIsSaving(true);
    const res = await mainApi.create_book_cafe_reservation({
      reservDate: selectedDate,
      slots: selectedSlots,
      requestId: `${userState.userId}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      userId: userState.userId,
      userName: userState.userName,
      userAddr: userState.readAddress,
      inputDt: getFormattedDateTime().compactDateTime,
    });
    if (res.code === "200") {
      setIsConfirmOpen(false);
      navigate("/reservationbookcafeend");
    } else if (res.code === "301") {
      window.alert("선택한 시간에 다른 예약이 완료되었습니다. 예약 현황을 다시 불러옵니다.");
      const availability = await mainApi.get_book_cafe_day_availability({ date: selectedDate });
      setReservedSlots(availability.code === "200" ? availability.response.reservedSlots ?? [] : []);
      setSelectedSlots([]);
      setIsConfirmOpen(false);
    } else if (res.code === "608") {
      window.alert("현재 북카페 예약은 오픈 준비 중입니다.");
      setIsConfirmOpen(false);
    } else {
      window.alert(res.response?.error_msg || "예약을 완료하지 못했습니다.");
    }
    setIsSaving(false);
  };

  const regularHourlyPrice = policy?.regularHourlyPrice ?? 10000;
  const hourlyPrice = policy?.hourlyPrice ?? 5000;
  const discountRate = Math.round((1 - hourlyPrice / regularHourlyPrice) * 100);
  const estimatedPrice = selectedSlots.length * hourlyPrice;

  return (
    <Stack className="hpoint-renewal hp-reservation-page" spacing={2} sx={{ maxWidth: "40rem", mx: "auto", p: 2 }}>
      <Box className="hp-screen-hero">
        <Typography component="span" variant="overline">BOOK CAFE</Typography>
        <Typography component="h1" className="hp-page-title">북카페 예약</Typography>
        <Box className="hp-book-cafe-rolling" aria-label="북카페 이용 안내">
          <Box className="hp-book-cafe-rolling-track">
            {BOOK_CAFE_ROLLING_MESSAGES.map((message) => (
              <span key={message.title}>
                <strong>{message.title}</strong>
                <span>{message.description}</span>
              </span>
            ))}
            <span aria-hidden="true">
              <strong>{BOOK_CAFE_ROLLING_MESSAGES[0].title}</strong>
              <span>{BOOK_CAFE_ROLLING_MESSAGES[0].description}</span>
            </span>
          </Box>
        </Box>
      </Box>

      <Alert severity="info">
        <Stack spacing={0.25}>
          <Typography variant="body2">
            매일 {BOOK_CAFE_PUBLIC_OPEN_TIME} ~ {BOOK_CAFE_PUBLIC_CLOSE_TIME} 운영
          </Typography>
          <Box className="hp-book-cafe-policy-card__price">
            <Typography component="div" variant="body2">
              1시간 정상가{" "}
              <del className="hp-book-cafe-policy-card__regular-price">
                {regularHourlyPrice.toLocaleString("ko-KR")}원
              </del>
            </Typography>
            {discountRate > 0 ? (
              <>
                <Box className="hp-book-cafe-policy-card__discount-offer">
                  <span>한시적 {discountRate}% 할인</span>
                  <strong className="hp-book-cafe-policy-card__discount-price">
                    {hourlyPrice.toLocaleString("ko-KR")}원
                  </strong>
                </Box>
                <Typography variant="caption">할인 일정은 운영 상황에 따라 변경될 수 있습니다.</Typography>
              </>
            ) : null}
          </Box>
          {getAvailabilityStatus(policy) === "OPENING_SOON" ? (
            <Typography variant="body2">오픈일 미정</Typography>
          ) : null}
        </Stack>
      </Alert>

      {!isLoading && !policy?.bookingEnabled ? (
        <Alert severity="warning" role="status">
          {getAvailabilityStatus(policy) === "UNDER_REPAIR"
            ? "현재 수리중입니다. 예약 신청이 잠시 중단되었습니다."
            : "오픈 준비 중입니다. 서비스 안내만 먼저 확인할 수 있어요."}
        </Alert>
      ) : null}
      {!isLoading && !policy ? <Alert severity="error">예약 정책을 불러오지 못했습니다.</Alert> : null}

      <Box className="hp-card hp-calendar-card" data-testid="book-cafe-calendar" sx={{ p: 2 }}>
        <Typography component="h2" variant="subtitle1" sx={{ mb: 1 }}>날짜 선택</Typography>
        {policy ? (
          <Calendar
            calendarType="gregory"
            className="custom-calendar hp-calendar"
            minDate={minDate}
            maxDate={maxDate}
            value={selectedDate ? parseDateKey(selectedDate) : minDate}
            formatDay={(_locale, date) => String(date.getDate())}
            tileDisabled={({ date, view }) =>
              !policy.bookingEnabled || (view === "month" && (date < minDate || date > maxDate))
            }
            onClickDay={(date) => setSelectedDate(formatDateKey(date))}
          />
        ) : <CircularProgress size={24} />}
      </Box>

      <Box className="hp-card" sx={{ p: 2 }}>
        <Typography component="h2" variant="subtitle1">시간 선택</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          여러 시간을 예약하려면 연결된 시간만 선택해 주세요. 한 번에 최대 {policy?.maxContiguousHours ?? 5}시간까지 가능합니다.
        </Typography>
        <Box
          data-testid="book-cafe-time-grid"
          aria-busy={isAvailabilityLoading}
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gridAutoRows: "var(--hp-control-height-sm)",
            gap: 1,
          }}
        >
          {isAvailabilityLoading
            ? slots.map((slot) => (
                <Skeleton
                  key={slot}
                  data-testid="book-cafe-slot-skeleton"
                  aria-hidden="true"
                  variant="rounded"
                  height="100%"
                />
              ))
            : slots.map((slot) => {
              const isSelected = selectedSlots.includes(slot);
              const isReserved = reservedSlots.includes(slot);
              const disabled = !policy?.bookingEnabled || isReserved || isPastSlot(slot);
              return (
                <Button
                  key={slot}
                  className="hp-book-cafe-time-slot"
                  aria-label={`${slot} ~ ${addHour(slot)}${isReserved ? " 예약됨" : ""}`}
                  aria-pressed={isSelected}
                  disabled={disabled}
                  variant={isSelected ? "contained" : "outlined"}
                  onClick={() => toggleSlot(slot)}
                >
                  <span className="hp-book-cafe-time-slot__time">{slot} ~ {addHour(slot)}</span>
                  {isReserved && <span className="hp-book-cafe-time-slot__badge" aria-hidden="true">예약</span>}
                </Button>
              );
            })}
        </Box>
      </Box>

      <Box className="hp-card hp-card--compact" sx={{ p: 2 }}>
        <Typography variant="body2">{selectedDate ? formatDateLabel(selectedDate) : "날짜를 선택해 주세요."}</Typography>
        <Typography variant="body2">{selectedSlots.length > 0 ? selectedSlots.map((slot) => `${slot}~${addHour(slot)}`).join(", ") : "시간을 선택해 주세요."}</Typography>
        <Typography data-testid="book-cafe-estimated-price" variant="h6">{estimatedPrice.toLocaleString("ko-KR")}원</Typography>
      </Box>

      <Stack spacing={1}>
        <Button
          className="hp-button hp-button--primary hp-sticky-cta"
          variant="contained"
          disabled={!policy?.bookingEnabled || selectedSlots.length === 0 || isAvailabilityLoading || isSaving}
          onClick={() => setIsConfirmOpen(true)}
        >{getReservationButtonLabel(policy)}</Button>
        <Button variant="outlined" onClick={() => navigate("/reservationbookcafeend")}>예약 내역</Button>
      </Stack>

      <Dialog
        className="hp-dialog hp-book-cafe-confirm-dialog"
        open={isConfirmOpen}
        PaperProps={{
          "aria-label": "북카페를 예약하시겠어요?",
          className: "hp-dialog__paper hp-book-cafe-confirm-dialog__paper",
        }}
        onClose={() => !isSaving && setIsConfirmOpen(false)}
      >
        <DialogTitle
          className="hp-book-cafe-confirm-dialog__header"
        >
          <Typography component="span" className="hp-book-cafe-confirm-dialog__eyebrow">
            예약 정보 확인
          </Typography>
          <Typography
            component="span"
            className="hp-book-cafe-confirm-dialog__title"
          >
            북카페를 예약하시겠어요?
          </Typography>
        </DialogTitle>
        <DialogContent className="hp-book-cafe-confirm-dialog__body">
          <Box className="hp-book-cafe-confirm-dialog__details">
            <Box className="hp-book-cafe-confirm-dialog__detail">
              <Typography component="span" className="hp-book-cafe-confirm-dialog__label">
                이용 날짜
              </Typography>
              <Typography component="strong" className="hp-book-cafe-confirm-dialog__date">
                {formatDateLabel(selectedDate)}
              </Typography>
            </Box>
            <Box className="hp-book-cafe-confirm-dialog__detail">
              <Typography component="span" className="hp-book-cafe-confirm-dialog__label">
                선택 시간
              </Typography>
              <Box className="hp-book-cafe-confirm-dialog__times">
                {selectedSlots.map((slot) => (
                  <Box component="span" className="hp-book-cafe-confirm-dialog__time" key={slot}>
                    {slot}~{addHour(slot)}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Box className="hp-book-cafe-confirm-dialog__total">
            <Box className="hp-book-cafe-confirm-dialog__total-copy">
              <Typography component="span">총 결제 금액</Typography>
              <Typography component="small">{selectedSlots.length}시간 이용</Typography>
            </Box>
            <Typography
              component="strong"
              className="hp-book-cafe-confirm-dialog__price"
              data-testid="book-cafe-confirm-price"
            >
              {estimatedPrice.toLocaleString("ko-KR")}원
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={isSaving} onClick={() => setIsConfirmOpen(false)}>취소</Button>
          <Button disabled={isSaving} onClick={saveReservation}>예약 확정</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ReservationBookCafe;
